import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useContact } from './ContactContext';
import { apiHeaders } from '../lib/consoleApi';
import { mergeLines, roleSentiment, linesFromMock, feedResetFor, POLL_MS } from '../lib/transcriptFeed';

// Phase 3 (2026-09-14): the ONE poller for the voice Contact Lens feed.
//
// Polls knktr-live-transcript (GET /transcript?contactId=…) every 3 s while a
// VOICE contact is connected, reads once more at ACW entry (to pick up the
// post-contact summary), then stops — polling after the contact clears is
// what produced the 2026-09-03 "resource cannot be found" errors. Feeds the
// LiveTranscript panel and the left-rail sentiment pills from the same
// state. Lives in its own provider so ContactContext (Andrew's layer, edited
// 2026-09-13) is not touched.
//
// Not wired for CHAT: that is the V2 API / StartContactStreaming path, its own
// item (discovery doc 2026-09-03 §1.3). Chat and task contacts never poll.
const TRANSCRIPT_URL = import.meta.env.VITE_TRANSCRIPT_URL || '';
// After this many consecutive non-404 failures the poller stops for the
// contact rather than hammering a broken endpoint.
const MAX_CONSECUTIVE_ERRORS = 8;

const TranscriptContext = createContext(null);

const EMPTY = { lines: [], sentiment: { customer: null, agent: null }, postContactSummary: null, categories: [] };

export function TranscriptProvider({ children }) {
  const { display, acwActive, wrapContext, isDemo } = useContact();
  const contactId = display?.meta?.contactId ?? null;
  const channel = display?.meta?.channel ?? null;
  const voice = channel === 'VOICE';

  const [feed, setFeed] = useState(EMPTY);
  // idle | polling | waiting (404: analysis not started / expired) | stopped | forbidden | error | demo
  const [status, setStatus] = useState('idle');
  const [lastReadAt, setLastReadAt] = useState(null);
  const trackedId = useRef(null);      // contact whose feed we hold, survives display clearing
  const errors = useRef(0);
  const inFlight = useRef(false);

  const readOnce = useCallback(async (id) => {
    if (!id || !TRANSCRIPT_URL || inFlight.current) return null;
    inFlight.current = true;
    try {
      const res = await fetch(`${TRANSCRIPT_URL}?contactId=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: await apiHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) { errors.current = 0; return { status: 'waiting' }; }
      if (res.status === 403) return { status: 'forbidden' };
      if (!res.ok || data.ok === false) { errors.current += 1; return { status: 'error' }; }
      errors.current = 0;
      return { status: 'ok', data };
    } catch {
      errors.current += 1;
      return { status: 'error' };
    } finally {
      inFlight.current = false;
    }
  }, []);

  const apply = useCallback((result) => {
    if (!result) return;
    if (result.status === 'ok') {
      setFeed(prev => {
        const lines = mergeLines(prev.lines, result.data.lines);
        return {
          lines,
          sentiment: lines === prev.lines ? prev.sentiment : roleSentiment(lines),
          postContactSummary: result.data.postContactSummary ?? prev.postContactSummary,
          categories: result.data.categories ?? prev.categories,
        };
      });
      setLastReadAt(Date.now());
      setStatus('polling');
    } else if (result.status === 'waiting') {
      setStatus(s => (s === 'polling' ? 'polling' : 'waiting'));
    } else if (result.status === 'forbidden') {
      setStatus('forbidden');
    } else if (result.status === 'error' && errors.current >= MAX_CONSECUTIVE_ERRORS) {
      setStatus('error');
    }
  }, []);

  // New contact → reset. Contact gone and wrap-up over → clear (#127: the feed
  // used to survive into idle, so the rail showed the last call's sentiment
  // on "Waiting for next contact"). During wrap-up the feed is kept: the final
  // read below needs trackedId, and the wrap-up screen may still show it.
  // Demo mode seeds from the mock transcript, no polling.
  useEffect(() => {
    if (isDemo) {
      const lines = linesFromMock(display?.transcript);
      setFeed({ ...EMPTY, lines, sentiment: roleSentiment(lines) });
      setStatus('demo');
      return;
    }
    const action = feedResetFor({ contactId, trackedId: trackedId.current, acwActive });
    if (action === 'track') {
      trackedId.current = contactId;
      errors.current = 0;
      setFeed(EMPTY);
      setLastReadAt(null);
      setStatus(voice && TRANSCRIPT_URL ? 'waiting' : 'idle');
    } else if (action === 'clear') {
      trackedId.current = null;
      errors.current = 0;
      setFeed(EMPTY);
      setLastReadAt(null);
      setStatus('idle');
    }
  }, [contactId, voice, isDemo, acwActive, display?.transcript]);

  // Poll while connected on voice and not in ACW.
  const active = Boolean(contactId) && voice && !acwActive && !isDemo && Boolean(TRANSCRIPT_URL);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const r = await readOnce(contactId);
      if (cancelled) return;
      apply(r);
      if (r?.status === 'forbidden' || (r?.status === 'error' && errors.current >= MAX_CONSECUTIVE_ERRORS)) {
        clearInterval(timer);
      }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [active, contactId, readOnce, apply]);

  // One final read at ACW entry, then stop. `display` may already be cleared
  // by CONTACT_ENDED, so the id comes from the wrap context or the tracked id.
  const finalDone = useRef(null);
  useEffect(() => {
    if (!acwActive || isDemo || !TRANSCRIPT_URL) return;
    const id = wrapContext?.contactId || trackedId.current;
    if (!id || finalDone.current === id) return;
    if (trackedId.current !== id) return;          // not a voice contact we were following
    finalDone.current = id;
    (async () => {
      const r = await readOnce(id);
      // #127: if wrap-up was saved before this read came back, the feed has
      // already been cleared for idle — a late answer must not refill it.
      if (trackedId.current !== id) return;
      apply(r);
      setStatus(s => (s === 'forbidden' || s === 'error' ? s : 'stopped'));
    })();
  }, [acwActive, wrapContext?.contactId, isDemo, readOnce, apply]);

  const value = {
    ...feed,
    status,
    lastReadAt,
    enabled: Boolean(TRANSCRIPT_URL) || isDemo,
    live: status === 'polling',
  };
  return <TranscriptContext.Provider value={value}>{children}</TranscriptContext.Provider>;
}

export function useTranscript() {
  const ctx = useContext(TranscriptContext);
  return ctx ?? { ...EMPTY, status: 'idle', lastReadAt: null, enabled: false, live: false };
}
